import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCreateReply } from '@/hooks/useForum';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useSpamProtection } from '@/hooks/useSpamProtection';
import { Send, LogIn } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ForumReplyFormProps {
  topicId: number;
  onSuccess: () => void;
}

export function ForumReplyForm({ topicId, onSuccess }: ForumReplyFormProps) {
  const [content, setContent] = useState('');

  const { toast } = useToast();
  const createReply = useCreateReply();
  const { user, profile, isAuthenticated } = useAuth();
  const { isSpam, HoneypotField } = useSpamProtection();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated || !profile) {
      toast({
        title: 'Du måste vara inloggad',
        description: 'Logga in för att svara.',
        variant: 'destructive',
      });
      return;
    }

    // Check for spam/bot behavior
    if (isSpam()) {
      toast({
        title: 'Kunde inte skicka',
        description: 'Vänta några sekunder och försök igen.',
        variant: 'destructive',
      });
      return;
    }

    if (!content.trim()) {
      toast({
        title: 'Skriv ett meddelande',
        description: 'Meddelandet kan inte vara tomt.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await createReply.mutateAsync({
        topicId,
        content: content.trim(),
        authorName: profile.display_name,
      });

      toast({
        title: 'Svar publicerat!',
        description: 'Ditt svar har lagts till i tråden.',
      });

      setContent('');
      onSuccess();
    } catch (error) {
      toast({
        title: 'Fel',
        description: 'Kunde inte publicera svar. Försök igen.',
        variant: 'destructive',
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="pt-4 border-t text-center py-6">
        <p className="text-muted-foreground text-sm mb-3">
          Du måste vara inloggad för att svara.
        </p>
        <Link to="/auth">
          <Button size="sm" variant="outline" className="gap-1.5">
            <LogIn className="h-3.5 w-3.5" />
            Logga in
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-4 border-t">
      <form onSubmit={handleSubmit} className="space-y-3">
        <HoneypotField />
        
        <Textarea
          id="replyContent"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={`Svara som ${profile?.display_name}...`}
          rows={3}
          required
          className="resize-none"
        />

        <div className="flex justify-end">
          <Button type="submit" disabled={createReply.isPending} size="sm" className="gap-1.5">
            <Send className="h-3.5 w-3.5" />
            {createReply.isPending ? 'Skickar...' : 'Skicka svar'}
          </Button>
        </div>
      </form>
    </div>
  );
}
